import React, { useId, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { DevTool } from "@hookform/devtools";
import { signInWithEmailAndPassword } from "firebase/auth";
import { collection, getDocs, query, where } from "firebase/firestore";
import { auth, db } from "../firebase.config";
import { useDispatch } from "react-redux";
import { editUser } from "../Redux/User/userSlice";
import { setLoading } from "../Redux/Loader/loadingSlice";
import { FaExclamationCircle } from "react-icons/fa";

const Login = () => {
  const navigate = useNavigate();
  const id = useId();
  const dispatch = useDispatch();

  const {
    register,
    control,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({ mode: "onSubmit" });

  const [authError, setAuthError] = useState("");

  const onSubmit = async (data) => {
    setAuthError("");
    dispatch(setLoading(true));
    try {
      const res = await signInWithEmailAndPassword(
        auth,
        data.username,
        data.password
      );
      const user = res.user;

      // load user doc
      const q = query(collection(db, "users"), where("uid", "==", user.uid));
      const snap = await getDocs(q);
      const docData = snap.docs?.[0]?.data();
      if (docData) {
        dispatch(editUser({ ...docData, loggedIn: true }));
      }
      navigate("/dashboard");
    } catch (err) {
      console.error("[Login] sign-in failed:", err);
      setAuthError("Username or password is invalid.");
    } finally {
      dispatch(setLoading(false));
    }
  };

  return (
    <div>
      <h2>Login</h2>

      {/* Top-level auth error, reusing your error styling */}
      {authError && (
        <div
          className="job-form-input-container error"
          style={{ marginBottom: 12 }}
        >
          <p className="error-message" role="alert">
            <span style={{ marginRight: 5 }}>
              <FaExclamationCircle />
            </span>
            {authError}
          </p>
        </div>
      )}

      <form className="login-form" onSubmit={handleSubmit(onSubmit)} noValidate>
        {/* EMAIL */}
        <div
          className={
            errors.username
              ? "job-form-input-container error"
              : "job-form-input-container"
          }
        >
          {errors.username && (
            <p className="error-message" role="alert">
              <span style={{ marginRight: 5 }}>
                <FaExclamationCircle />
              </span>
              {errors.username.message}
            </p>
          )}

          <div className="job-form-input">
            <label className="nowrap" htmlFor={`username-${id}`}>
              Email:
            </label>
            <input
              id={`username-${id}`}
              type="email"
              placeholder="Email"
              aria-invalid={!!errors.username}
              {...register("username", {
                required: { value: true, message: "Email is required" },
                pattern: {
                  value:
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  message: "Email format is invalid",
                },
              })}
            />
          </div>
        </div>

        {/* PASSWORD */}
        <div
          className={
            errors.password
              ? "job-form-input-container error"
              : "job-form-input-container"
          }
        >
          {errors.password && (
            <p className="error-message" role="alert">
              <span style={{ marginRight: 5 }}>
                <FaExclamationCircle />
              </span>
              {errors.password.message}
            </p>
          )}

          <div className="job-form-input">
            <label className="nowrap" htmlFor={`password-${id}`}>
              Password:
            </label>
            <input
              id={`password-${id}`}
              type="password"
              placeholder="Password"
              aria-invalid={!!errors.password}
              {...register("password", {
                required: { value: true, message: "Password is required" },
              })}
            />
          </div>
        </div>

        <button
          className="login-btn"
          type="submit"
          disabled={isSubmitting}
          aria-disabled={isSubmitting}
          title="Login"
        >
          {isSubmitting ? "Logging in…" : "Login"}
        </button>
      </form>

      <p className="forgot-pass-text">
        <Link to="/forgot-password">Forgot Password</Link>
      </p>

      {/* Keep DevTool for debugging */}
      <DevTool control={control} />
    </div>
  );
};

export default Login;
